import type { ReactNode } from 'react';
import LineChart, { type LineChartProps } from './LineChart';
import MetricCard, { type MetricCardProps } from './MetricCard';
import PageTitle from './PageTitle';
import QuickAccessTile, { type QuickAccessTileProps } from './QuickAccessTile';
import Sidebar, { type SidebarItem } from './Sidebar';
import Topbar from './Topbar';
import HeroGlow from './HeroGlow';

type DashboardLayoutProps = {
  sidebar: { brand: string; items: SidebarItem[] };
  user: { name: string; role: string };
  pageTitle: { heading: string; sub: string };
  metrics: MetricCardProps[];
  chart: LineChartProps;
  quickAccess: QuickAccessTileProps[];
  /**
   * Optional content rendered inside the Topbar's `extra` slot (e.g. a
   * settings-panel gear button). If omitted, the slot is empty.
   */
  settingsPanel?: ReactNode;
  /**
   * Optional content rendered as a sibling of `.axi-main` (e.g. the
   * mobile hamburger toggle + backdrop). Sits at the dashboard root
   * level so its `position: fixed` is not clipped by the main grid.
   */
  mobileMenu?: ReactNode;
  /**
   * Whether the sidebar is currently in mobile-drawer `is-open` state.
   * Forwarded to `<Sidebar>` so the CSS `.axi-sidebar.is-open` rule
   * (defined in the `<= 768px` media block) flips the transform.
   */
  sidebarOpen?: boolean;
  /**
   * Optional override for which sidebar item renders as the active
   * pill. When provided, the matching item gets `active: true` and
   * every other item gets `active: false`. When omitted, the page
   * must declare `active: true` inside its own `sidebar.items` array
   * (the legacy path used by `ExamplesDashboard.test.tsx`).
   */
  activeSidebarId?: string;
  /**
   * Optional children rendered inside `<main>` as the page-specific
   * body. When provided, the default `metrics` + `chart` +
   * `quickAccess` layout is skipped and `children` takes over. This
   * is the path used by `/app/people` / `/app/payroll` / etc., which
   * need their own composition (PageHeader + SectionCard + DataTable)
   * instead of the workbench's metric grid.
   *
   * The `pageTitle` is still rendered above the children (or above
   * the default metric grid) so navigation context stays uniform.
   */
  children?: ReactNode;
  /**
   * When true, the built-in `<PageTitle>` is suppressed so the page
   * can render its own `<PageHeader>` from inside `children`. Used by
   * the `/app/*` business pages that want an actions slot next to
   * the heading.
   */
  omitPageTitle?: boolean;
};

function DashboardLayout({
  sidebar,
  user,
  pageTitle,
  metrics,
  chart,
  quickAccess,
  settingsPanel,
  mobileMenu,
  sidebarOpen,
  activeSidebarId,
  children,
  omitPageTitle
}: DashboardLayoutProps) {
  // If `activeSidebarId` is provided, derive the `active` flag from it
  // before handing the items to `<Sidebar>` so callers do not have to
  // pre-mutate the data they pass in.
  const derivedItems = activeSidebarId
    ? sidebar.items.map((item) => ({
        ...item,
        active: item.id === activeSidebarId
      }))
    : sidebar.items;

  return (
    <div className="axi-dashboard" data-axi-dashboard="black-gold">
      <Sidebar brand={sidebar.brand} items={derivedItems} isOpen={sidebarOpen} />
      <main className="axi-main" aria-labelledby="axi-page-title">
        <HeroGlow />
        <Topbar user={user} extra={settingsPanel} />
        {!omitPageTitle ? <PageTitle heading={pageTitle.heading} sub={pageTitle.sub} /> : null}
        {children ? (
          <>{children}</>
        ) : (
          <>
            <section className="axi-section" aria-label="关键指标">
              <div className="axi-metric-grid">
                {metrics.map((metric, idx) => (
                  <MetricCard key={`${metric.label}-${idx}`} {...metric} />
                ))}
              </div>
            </section>
            <div className="axi-lower-grid">
              <section className="axi-section" aria-label="趋势图表">
                <LineChart {...chart} />
              </section>
              <section className="axi-section" aria-label="快捷入口">
                <div className="axi-quick-tile-grid">
                  {quickAccess.map((action) => (
                    <QuickAccessTile key={action.id} {...action} />
                  ))}
                </div>
              </section>
            </div>
          </>
        )}
      </main>
      {mobileMenu}
    </div>
  );
}

export default DashboardLayout;